/*
    Copyright 2020 Lauren Gyldenlove.
    This file is part of Duelmaster.

    Duelmaster is free software: you can redistribute it and/or modify
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Duelmaster is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with Foobar.  If not, see <https://www.gnu.org/licenses/>.
 */

import React from 'react';
import { AppContext } from '../App';
import { AppSettings } from '../model/AppSettings';

function apply(
    shouldApply: boolean,
    hasOverflown: boolean,
    damagePerTick: number,
    damageApplied: number,
    setDamageApplied: Function,
    totalDamage: number,
    playAudio: Function,
    stopAudio: Function
) {
    if(shouldApply) {
        playAudio();
        setTimeout(() => setDamageApplied(damageApplied - damagePerTick), 10);
    } else if(hasOverflown) {
        setDamageApplied(totalDamage);
    }

    stopAudio();
}

function resetDuel(initialAmount: number, setDamageApplied: Function) {
    setDamageApplied(initialAmount);
}


export interface LifePointCounterProps {
    initialAmount: number;
    damage: number;
    lastDamageAmount: number;
}

export const LifePointCounter = (props: LifePointCounterProps) => { 
    const style = ["life-point-counter-value"];
    const appSettings: AppSettings = React.useContext(AppContext);

    const pointDropSound = new Audio("./audio/free-drop.mp3");
    pointDropSound.volume = appSettings.volume / 100;

    const totalDamage = props.initialAmount - props.damage;
    const [damageApplied, setDamageApplied] = React.useState(props.initialAmount);
    const [audioPlaying, setAudioPlaying] = React.useState(false);

    const playAudio = React.useCallback(() => {
        if(!audioPlaying) {
            pointDropSound.play();
            setAudioPlaying(true);
        }        
    }, [audioPlaying, pointDropSound, setAudioPlaying]);

    const stopAudio = React.useCallback(() => {
        if(damageApplied === totalDamage) {
            setAudioPlaying(false);
        }        
    }, [damageApplied, totalDamage, setAudioPlaying]);

    React.useEffect(() => {
        const damagePerTick = Math.ceil(props.lastDamageAmount / 70);
        
        if(props.lastDamageAmount > 0) {
            const shouldApply = damageApplied > totalDamage;
            const hasOverflown = damageApplied < totalDamage;
            apply(shouldApply, hasOverflown, damagePerTick, damageApplied, setDamageApplied, totalDamage, playAudio, stopAudio);
        } else if (props.lastDamageAmount < 0) {
            const shouldApply = damageApplied < totalDamage;
            const hasOverflown = damageApplied > totalDamage;
            apply(shouldApply, hasOverflown, damagePerTick, damageApplied, setDamageApplied, totalDamage, playAudio, stopAudio);
        } else if (props.damage === 0 && props.lastDamageAmount === 0) {
            resetDuel(props.initialAmount, setDamageApplied);
        }

    }, [ 
        pointDropSound,
        audioPlaying, 
        damageApplied, 
        props.lastDamageAmount, 
        props.initialAmount, 
        props.damage, 
        totalDamage,
        appSettings.volume,
        playAudio,
        stopAudio
    ]);

    if(damageApplied < 1) {
        style.push(" loss");
    }
    
    return (
        <div className="life-point-counter">
            <div className={style.join(" ")}>{damageApplied}</div>
        </div>
    );
}
